import React, { useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Globe,
  Mail,
  MessageCircle,
  MonitorSmartphone,
  Send,
  ShieldCheck,
  Sparkles,
  Users,
  Video,
} from 'lucide-react';
import AppHeader from './AppHeader';
import { Link } from 'react-router-dom';

const SOBRE_WHATSAPP_URL =
  'https://api.whatsapp.com/send/?phone=5513997701755&text=Ol%C3%A1%21%20Gostaria%20de%20saber%20mais%20sobre%20a%20Constela%20Brasil.';

const sobrePlatformFeatures = [
  { icon: Globe, title: 'Cenário virtual imersivo', text: 'Um ambiente pensado para conduzir constelações com profundidade e organização.' },
  { icon: Users, title: 'Avatares interativos', text: 'Representantes e participantes no campo, com movimentação intuitiva.' },
  { icon: Video, title: 'Videochamada integrada', text: 'Tudo na mesma sala, sem precisar alternar entre aplicativos.' },
  { icon: Sparkles, title: 'Visualização do campo', text: 'Veja o campo completo e conduza sessões individuais ou em grupo.' },
];

const sobreEasePoints = ['não precisa instalar', 'intuitiva e fácil de usar', 'funciona no celular'];

const sobreForwardActions = [
  { label: 'Conhecer os planos', description: 'Horas avulsas ou planos mensais, sem mensalidade obrigatória.', to: '/planos?secao=terapeuta', primary: true },
  { label: 'Agendar uma sessão', description: 'Encontre um constelador homologado e marque sua constelação.', to: '/terapeutas', primary: false },
  { label: 'Fale conosco', description: 'Tire dúvidas com nossa equipe pelo formulário de contato.', to: '/contato', primary: false },
];

export const SobreNosPage: React.FC = () => (
  <div className="min-h-screen bg-[#F3F6F1] text-[#3A5859]">
    <AppHeader />
    <main>
      <section className="relative overflow-hidden pt-28 pb-14 sm:pt-32 sm:pb-16">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#E8EFEB] via-[#F3F6F1] to-[#EDE4EA]/40" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-[#A56096]">Constelação Familiar Online</p>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Sobre <span className="text-[#A56096]">Nós</span>
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-[#496566] sm:text-xl">
            A Constela Brasil é uma plataforma desenvolvida exclusivamente para Constelação Familiar online, tanto em grupo como individual.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">Tudo em um só lugar</h2>
            <p className="mx-auto mt-3 max-w-2xl text-[#496566]">
              Ela reúne em um só lugar os recursos essenciais para uma experiência profissional no online.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {sobrePlatformFeatures.map((feature) => (
              <article key={feature.title} className="rounded-[1.5rem] border border-[#E8EFEB] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                <div className="mb-4 inline-flex rounded-2xl bg-[#A56096]/10 p-3">
                  <feature.icon className="h-6 w-6 text-[#A56096]" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#496566]">{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="space-y-5 text-[#496566]">
              <p className="text-lg leading-relaxed">A ideia da plataforma é facilitar os atendimentos online de forma prática, intuitiva e profissional.</p>
              <p className="leading-relaxed">Ela ajuda tanto quem já atende quanto quem está começando, trazendo uma estrutura profissional e a possibilidade de facilitar grupos a distância contando com a visualização do campo.</p>
              <p className="leading-relaxed">Você também pode ampliar sua área de atendimento, aumentando as possibilidades de agendamento, além de utilizar a plataforma para cursos e supervisão de alunos.</p>
              <p className="leading-relaxed">A contratação pode ser por hora, sem mensalidade obrigatória, ou através de planos mensais. Após a contratação, as horas são liberadas imediatamente.</p>
              <p className="font-medium text-[#3A5859]">Você terá uma sala exclusiva sempre disponível. Basta mandar o link da sua sala para os clientes e eles entram direto na videochamada integrada.</p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#A56096]/20 to-[#3A5859]/10 blur-2xl" aria-hidden="true" />
              <img src="/assets/checkout/plataforma-preview.png" alt="Prévia da plataforma Constela Brasil com campo virtual e videochamada" className="relative w-full rounded-[1.5rem] border border-[#E8EFEB] shadow-xl" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="rounded-[1.5rem] border border-[#E8EFEB] bg-white p-8 shadow-sm lg:col-span-1">
              <div className="mb-4 inline-flex rounded-2xl bg-[#3A5859]/10 p-3">
                <MonitorSmartphone className="h-6 w-6 text-[#3A5859]" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold">Simples de usar</h2>
              <ul className="mt-5 space-y-3">
                {sobreEasePoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-[#496566]">
                    <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#A56096]" aria-hidden="true" />
                    <span className="capitalize">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-[1.5rem] border border-[#E8EFEB] bg-white p-8 shadow-sm lg:col-span-2">
              <p className="text-lg leading-relaxed text-[#496566]">E você ainda conta com tutorial dentro da plataforma e suporte pelo WhatsApp para tirar dúvidas.</p>
              <a href={SOBRE_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#20BA5A]">
                <MessageCircle className="h-5 w-5" aria-hidden="true" />
                Falar no WhatsApp
              </a>
              <div className="mt-8 rounded-2xl border border-[#A56096]/20 bg-[#A56096]/5 p-6">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-[#A56096]" aria-hidden="true" />
                  <div>
                    <h3 className="text-lg font-bold text-[#3A5859]">7 dias de garantia</h3>
                    <p className="mt-2 text-[#496566]">Pode experimentar e usar a plataforma e, se achar que não é para você, devolvemos o seu dinheiro.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-[#3A5859] to-[#496566] py-14 sm:py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Próximos passos</h2>
            <p className="mx-auto mt-3 max-w-2xl text-white/80">Escolha o caminho que faz mais sentido para você agora.</p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {sobreForwardActions.map((action) => (
              <Link key={action.to} to={action.to} className={`group flex h-full flex-col rounded-[1.5rem] border p-6 transition-all duration-300 hover:-translate-y-1 ${action.primary ? 'border-[#A56096]/40 bg-[#A56096] text-white shadow-lg shadow-[#A56096]/25 hover:bg-[#945586]' : 'border-white/15 bg-white/10 text-white backdrop-blur hover:bg-white/15'}`}>
                <h3 className="text-lg font-bold">{action.label}</h3>
                <p className={`mt-2 flex-1 text-sm leading-relaxed ${action.primary ? 'text-white/90' : 'text-white/75'}`}>{action.description}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold">
                  Continuar
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  </div>
);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const productionHosts = [
        'constelabrasil.com.br',
        'www.constelabrasil.com.br',
        'app.constelabrasil.com.br',
      ];

      const env = productionHosts.includes(window.location.hostname) ? 'Produção' : 'Desenvolvimento';

      const data = {
        accessKey: "sf_8bcbe62al9d9fj457dcd82mh",
        subject: "Constela Brasil - Contato Via Site [" + env + "] - " + formData.subject,
        replyTo: "@",
        name: formData.name,
        email: formData.email,
        message: formData.message,
      };

      const url = "https://api.staticforms.xyz/submit";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };
  const whatsappNumber = '5513997701755';
  const whatsappMessage = encodeURIComponent('Olá! Gostaria de saber mais sobre a Constela Brasil.');
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <div className="min-h-screen bg-[#F3F6F1]">
      <AppHeader />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-[#F3F6F1]">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3A5859] mb-6 leading-tight">
            Fale <span className="text-[#A56096]">Conosco</span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-[#496566] mb-8 max-w-3xl mx-auto">
            Estamos aqui para ajudar. Entre em contato conosco!
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Email Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-16 h-16 bg-[#A56096]/10 rounded-full mb-6">
                <Mail className="w-8 h-8 text-[#A56096]" />
              </div>
              <h3 className="text-2xl font-bold text-[#3A5859] mb-4">
                Email
              </h3>
              <p className="text-[#496566] mb-6">
                Envie-nos uma mensagem detalhada e responderemos o mais breve possível.
              </p>
              <a 
                href="mailto:contato@constelabrasil.com.br"
                className="text-[#A56096] font-semibold hover:text-[#8B4A7D] transition-colors"
              >
                contato@constelabrasil.com.br
              </a>
            </div>

            {/* WhatsApp Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-16 h-16 bg-[#25D366]/10 rounded-full mb-6">
                <MessageCircle className="w-8 h-8 text-[#25D366]" />
              </div>
              <h3 className="text-2xl font-bold text-[#3A5859] mb-4">
                WhatsApp
              </h3>
              <p className="text-[#496566] mb-6">
                Fale conosco diretamente pelo WhatsApp para um atendimento mais rápido.
              </p>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#20BA5A] transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Enviar Mensagem
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold text-[#3A5859] mb-8 text-center">
              Envie sua Mensagem
            </h2>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-green-800">
                  Mensagem enviada com sucesso! Entraremos em contato em breve.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-800">
                  Ocorreu um erro ao enviar a mensagem. Por favor, tente novamente ou entre em contato pelo WhatsApp.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <input type="text" name="honeypot" style={{display: "none"}} />
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#3A5859] mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A56096] focus:border-transparent outline-none transition-all"
                    placeholder="Seu nome"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#3A5859] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A56096] focus:border-transparent outline-none transition-all"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-[#3A5859] mb-2">
                  Assunto *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A56096] focus:border-transparent outline-none transition-all"
                  placeholder="Como podemos ajudar?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#3A5859] mb-2">
                  Mensagem *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A56096] focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Escreva sua mensagem aqui..."
                />
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 bg-[#A56096] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#8B4A7D] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#3A5859] mb-4">
            Procurando por Respostas Rápidas?
          </h2>
          <p className="text-[#496566] mb-8">
            Muitas dúvidas já foram respondidas em nossa página inicial
          </p>
          <Link
            to="/#faq"
            className="inline-flex items-center gap-2 bg-[#3A5859] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2A4849] transition-colors"
          >
            Ver Perguntas Frequentes
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Contact;
